import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

import { AppLink } from '@/components/atoms';
import { fetchTikTokOembed } from '@/services/tiktokOembed';

type Props = { url: string };

type State = { status: 'loading' } | { status: 'ready'; html: string } | { status: 'error' };

/**
 * The live TikTok player. Mounting this component IS the "load" step of the
 * click-to-load facade that sits in front of it — nothing here runs until
 * this is on screen, which is the whole reason the facade and this player
 * are two separate components instead of one with a `loaded` flag: a
 * conditionally-mounted child cannot fire its effect early by accident the
 * way a conditionally-skipped one inside a single component can.
 */
export function TikTokEmbed({ url }: Props) {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    fetchTikTokOembed(url)
      .then((embed) => {
        if (!cancelled) setState({ status: 'ready', html: embed.html });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (state.status === 'loading') {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          bgcolor: 'common.black',
        }}
      >
        <CircularProgress size={28} sx={{ color: 'common.white' }} aria-label="Loading clip" />
      </Box>
    );
  }

  if (state.status === 'error') {
    return (
      <Stack
        spacing={2}
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          bgcolor: 'common.black',
          color: 'common.white',
          textAlign: 'center',
          p: 4,
        }}
      >
        <Typography variant="body2">This clip couldn’t be loaded.</Typography>
        <AppLink
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'inherit', textDecoration: 'underline', fontWeight: 700 }}
        >
          Open on TikTok
        </AppLink>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        bgcolor: 'common.black',
        '& iframe': { width: '100%', height: '100%', border: 0, display: 'block' },
      }}
      // `html` is TikTok's own oEmbed response for this exact link, relayed
      // untouched by our server-side proxy (supabase/functions/tiktok-oembed)
      // — not user input, and it is nothing more than a single <iframe>.
      dangerouslySetInnerHTML={{ __html: state.html }}
    />
  );
}
